/**
 * RenderReferenceText.tsx — PPP.3 Gap 2
 *
 * Splits a plain-text string on known domain abbreviations and wraps each
 * occurrence with <Term> so that Radix tooltips appear on hover.
 *
 * Usage:
 *   <RenderReferenceText text={obligation.referenceNote} />
 *
 * The component is intentionally kept in components/ui/ (alongside Term.tsx)
 * so that it can be imported by both ObligationEditor and CycleScheduleEditor
 * without creating a circular dependency.
 */

import { Term } from "./Term";

// ─── Abbreviation → tooltip-key map ──────────────────────────────────────────

/**
 * Abbreviations auto-wrapped with <Term> when they appear in referenceNote /
 * explanation text. Order matters: longest match first to avoid partial hits.
 * Keys must match tooltip.json.
 */
const TERM_TOKENS: { pattern: RegExp; key: string }[] = [
  { pattern: /\bAÜG\b/g,      key: "aug" },
  { pattern: /\bFZ-LLC\b/g,   key: "fz_llc" },
  { pattern: /\bSàrl\b/g,     key: "sarl" },
  { pattern: /\bSARL\b/g,     key: "sarl" },
  { pattern: /\bGmbH\b/g,     key: "gmbh" },
  { pattern: /\bREPSE\b/g,    key: "repse" },
  { pattern: /\bLKPM\b/g,     key: "lkpm" },
  { pattern: /\bNAR1\b/g,     key: "nar1" },
  { pattern: /\bDIAN\b/g,     key: "dian" },
  { pattern: /\bRTBF\b/g,     key: "rtbf" },
  { pattern: /\bCCSS\b/g,     key: "ccss" },
  { pattern: /\bCNSS\b/g,     key: "cnss" },
  { pattern: /\bKRS\b/g,      key: "krs" },
  { pattern: /\bMCA\b/g,      key: "mca" },
  { pattern: /\bAML\b/g,      key: "aml" },
  { pattern: /\bRAG\b/g,      key: "rag" },
  { pattern: /\bISO\b/g,      key: "iso" },
  { pattern: /\bIBC\b/g,      key: "ibc" },
  { pattern: /\bEOR\b/g,      key: "eor" },
  { pattern: /\bFYE\b/g,      key: "fye" },
  { pattern: /\bAGM\b/g,      key: "agm" },
  { pattern: /\bUBO\b/g,      key: "ubo" },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface RenderReferenceTextProps {
  text: string;
}

/**
 * Splits `text` on known abbreviations and wraps each with <Term>.
 * Preserves the caller's surrounding styling — returns a fragment, not a block.
 */
export function RenderReferenceText({ text }: RenderReferenceTextProps): JSX.Element {
  type Segment = string | { key: string; match: string };
  let segments: Segment[] = [text];

  for (const { pattern, key } of TERM_TOKENS) {
    const next: Segment[] = [];
    for (const seg of segments) {
      if (typeof seg !== "string") {
        next.push(seg);
        continue;
      }
      // Reset lastIndex for global regex before each use
      pattern.lastIndex = 0;
      const parts = seg.split(pattern);
      pattern.lastIndex = 0;
      const matches = seg.match(pattern) ?? [];
      parts.forEach((p, i) => {
        if (p) next.push(p);
        if (i < matches.length) next.push({ key, match: matches[i] });
      });
    }
    segments = next;
  }

  return (
    <>
      {segments.map((seg, i) =>
        typeof seg === "string" ? (
          <span key={i}>{seg}</span>
        ) : (
          <Term key={i} term={seg.key}>{seg.match}</Term>
        ),
      )}
    </>
  );
}
